interface AsyncLoaderOptions {
    uri: string;
    placement: string;
    signal: '' | 'visibleInViewport';
    loader: boolean;
}

const defaultOptions: AsyncLoaderOptions = {
    uri: '',
	placement: 'after',
    signal: '',
	loader: true
};

class AsyncLoader {
    private el: HTMLElement;
    private targetEl: HTMLElement;
    private options: AsyncLoaderOptions;
    private asyncSignal: string;
    private asyncUri: string;
    private asyncLoader: boolean;
    private placement: string;

    private asyncContentLoaded: boolean = false;

	constructor(el: HTMLElement, options: AsyncLoaderOptions | null = null) {
		this.el = el;
		this.options = Object.assign(defaultOptions, options || {});
		this.asyncUri = el.getAttribute('href') ?? el.dataset.asyncUri ?? this.options.uri;
		this.asyncSignal = el.dataset.asyncEvent ?? el.dataset.asyncSignal ?? this.options.signal;
		this.asyncLoader = parseInt(el.dataset.asyncLoader ?? '0') > 0 ? true : this.options.loader ?? false;
		this.placement = el.dataset.asyncPlacement ?? options?.placement ?? '';
		this.targetEl = (el.dataset.asyncTarget) ? document.querySelector(el.dataset.asyncTarget) as HTMLElement : el;
		if(this.asyncUri && this.asyncUri != '') {
			if(this.asyncSignal && this.asyncSignal != '') {
				switch(this.asyncSignal) {
					case 'visibleInViewport':
						this.el.setAttribute('data-loader', 'containerLoader');

						const intersectionObserver = new IntersectionObserver((entries) => {
							if (entries[0].intersectionRatio <= 0) return;
							this.loadContent();
						});
						intersectionObserver.observe(this.el);
					break;
					default:
						this.el.addEventListener(this.asyncSignal, (ev) => {
							this.loadContent();
							ev.preventDefault();
							return false;
						});
					break;
				}
			} else {
				this.loadContent();
			}
		}

	}

    public static load(el: HTMLElement, options: AsyncLoaderOptions|null = null): AsyncLoader {
        return new AsyncLoader(el, options);
    }

	private loadContent() {
		if(this.asyncContentLoaded) {
			return;
		}
		let loaderEl: HTMLElement;
		if(this.asyncLoader) {
			loaderEl = document.createElement('div');
			loaderEl.setAttribute('class', 'd-flex justify-content-center');
			loaderEl.innerHTML = '<div class="spinner-border text-th-gray" role="status"><span class="sr-only">Lade...</span></div>';
			this.el.parentNode?.insertBefore(loaderEl, this.el);
		}
		fetch(this.asyncUri).then((response: Response) => {
			if(!response.ok) {
				throw Error(response.statusText);
			}
			return response.text();
		}).then((content: string) => {
			if(loaderEl) {
				loaderEl.remove();
			}
			if(content.indexOf('<body') < 0) {
				switch(this.placement) {
					case 'before':
						this.targetEl.before(content);
					break;
					case 'after':
						this.targetEl.after(content);
					break;
					case 'prepend':
						this.targetEl.prepend(content);
					break;
					case 'append':
						this.targetEl.append(content);
					break;
					case 'inner':
						this.targetEl.innerHTML = content;
					break;
					default:
						this.targetEl.replaceWith(content);
					break;
				}
			}
			this.asyncContentLoaded = true;
			document.querySelector('body')?.dispatchEvent(new Event('asyncReady'));
		}).catch((ex) => {
			if(loaderEl) {
				loaderEl.remove();
			}
			if(ex.status && ex.status > 0) {
				window.ThiemeCore.errorHandler(
					window.ThiemeCore.Lang.translate('common:error.request.'+ex.status+'.message', null, 'An error occurred while loading the content.'),
					window.ThiemeCore.Lang.translate('common:error.request.'+ex.status+'.title', null, 'Error'),
					'warning',
					this.el
				);
			} else {
				window.ThiemeCore.errorHandler(
					window.ThiemeCore.Lang.translate('common:error.general.message', null, 'An error occurred while loading the content.'),
					window.ThiemeCore.Lang.translate('common:error.general.title', null, 'Error'),
					'warning',
					this.el
				);
			}
		});

	}
}


export { type AsyncLoaderOptions };
export default AsyncLoader;
